import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Team, TeamMember, Event, AddTeamMemberRequest, ParticipantResponse } from '../utils/types';
import { apiGet, apiDelete, apiPost, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useFileUploadFeature } from '../contexts/FeatureFlagContext';
import TeamFileManager from './TeamFileManager';
import AddTeamMember from './AddTeamMember';
import LocationSelector from './LocationSelector';
import EditTeam from './EditTeam';
import FeatureFlagToggle from './FeatureFlagToggle';

const TeamDetails: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const [team, setTeam] = useState<Team | null>(null);
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [eventLoading, setEventLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [uploadSuccess, setUploadSuccess] = useState<string>('');
  const [uploadError, setUploadError] = useState<string>('');
  const [removing, setRemoving] = useState<string>('');
  const [joining, setJoining] = useState<boolean>(false);
  const [showJoinLocationSelector, setShowJoinLocationSelector] = useState<boolean>(false);
  const [joinLocation, setJoinLocation] = useState<string>('');
  const [joinLocationOther, setJoinLocationOther] = useState<string>('');
  const [userCurrentTeam, setUserCurrentTeam] = useState<Team | null>(null);
  const [checkingTeamStatus, setCheckingTeamStatus] = useState<boolean>(false);
  const [showEditTeam, setShowEditTeam] = useState<boolean>(false);
  const { token, user, refreshUser } = useAuth();
  const fileUploadEnabled = useFileUploadFeature();

  // Check if current user has organizer role (case-insensitive)
  const isOrganizer = user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER') || false;
  
  // Check if current user is a member of this team
  const isTeamMember = team?.members?.some(member => 
    member.user_id === user?.user_id || member.email === user?.email
  ) || false;

    // Debug logging for team membership
  console.log('🔍 TeamDetails Debug Info:', {
    user: user ? { 
      id: user.user_id, 
      email: user.email, 
      roles: user.roles,
      current_teams: user.current_teams,
      has_current_teams_data: !!user.current_teams
    } : null,
    team: team ? { id: team.team_id, name: team.name, looking_for_members: team.looking_for_members, members: team.members } : null,
    isOrganizer,
    isTeamMember,
    userCurrentTeam: userCurrentTeam ? { id: userCurrentTeam.team_id, name: userCurrentTeam.name } : null,
    checkingTeamStatus,
    teamMembersCheck: team?.members?.map(member => ({
      user_id: member.user_id,
      email: member.email,
      display_name: member.display_name,
      matchesCurrentUser: member.user_id === user?.user_id || member.email === user?.email
    }))
  });

  // Helper function to load event details
  const loadEventDetails = async (eventId: string) => {
    if (!token) return;
    
    setEventLoading(true);
    try {
      const eventData = await apiGet<Event>(`/events/${eventId}`, token);
      setEvent(eventData);
    } catch (err) {
      console.error('Failed to load event details:', err);
      // Don't set error state for event loading failure - just log it
      // The team details should still be usable even if event details fail
    } finally {
      setEventLoading(false);
    }
  };

  // Check if user already has a team in this event
  const checkUserTeamStatus = async (eventId: string) => {
    if (!token || !user) {
      console.log('🔍 checkUserTeamStatus: No token or user available');
      return;
    }
    
    console.log('🔍 checkUserTeamStatus: Using auth context team data for user:', user.email, 'in event:', eventId);
    
    // Use team data from auth context if available
    if (user.current_teams) {
      console.log('🔍 checkUserTeamStatus: Found teams in auth context:', user.current_teams);
      
      const existingTeam = user.current_teams.find(team => team.event_id === eventId);
      console.log('🔍 checkUserTeamStatus: Existing team for this event:', existingTeam);
      
      if (existingTeam) {
        // Convert to Team format for compatibility
        setUserCurrentTeam({
          team_id: existingTeam.team_id,
          event_id: existingTeam.event_id,
          name: existingTeam.name,
          description: existingTeam.description,
          looking_for_members: existingTeam.looking_for_members,
          desired_skills: [],
          created_by: '',
          created_at: existingTeam.created_at,
          member_count: existingTeam.member_count
        });
        console.log('✅ User already has team:', existingTeam.name, 'in event:', eventId);
      } else {
        setUserCurrentTeam(null);
        console.log('✅ User does not have a team in event:', eventId);
      }
      return;
    }
    
    // Fallback to API call if no team data in auth context
    console.log('🔍 checkUserTeamStatus: No team data in auth context, falling back to API call');
    setCheckingTeamStatus(true);
    try {
      // Get user's teams and check if they have one for this event
      const userTeams = await apiGet<Team[]>('/teams', token);
      console.log('🔍 checkUserTeamStatus: User teams from API:', userTeams);
      
      const existingTeam = userTeams.find(team => team.event_id === eventId);
      console.log('🔍 checkUserTeamStatus: Existing team for this event:', existingTeam);
      
      setUserCurrentTeam(existingTeam || null);
      
      if (existingTeam) {
        console.log('✅ User already has team:', existingTeam.name, 'in event:', eventId);
      } else {
        console.log('✅ User does not have a team in event:', eventId);
      }
    } catch (err) {
      console.error('❌ checkUserTeamStatus: Failed to check user team status:', err);
      // Don't set error - this is optional information
    } finally {
      setCheckingTeamStatus(false);
    }
  };

  useEffect(() => {
    if (teamId && token) {
      loadTeam();
    }
  }, [teamId, token]);

  // Load event details when team is loaded
  useEffect(() => {
    if (team?.event_id && token) {
      loadEventDetails(team.event_id);
      // Also check if user already has a team in this event
      checkUserTeamStatus(team.event_id);
    }
  }, [team?.event_id, token]);

  const loadTeam = async () => {
    if (!teamId || !token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const teamData = await apiGet<Team>(`/teams/${teamId}`, token);
      setTeam(teamData);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to load team');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberToRemove: TeamMember) => {
    if (!token) return;
    
    // Confirm removal
    const confirmMessage = memberToRemove.display_name 
      ? `Are you sure you want to remove ${memberToRemove.display_name} from the team?`
      : `Are you sure you want to remove ${memberToRemove.email} from the team?`;
    
    if (!window.confirm(confirmMessage)) return;

    // Determine member identifier (use user_id if available, otherwise email)
    const memberIdentifier = memberToRemove.user_id || memberToRemove.email;
    
    setRemoving(memberIdentifier);
    
    try {
      await apiDelete(`/teams/${teamId}/members/${encodeURIComponent(memberIdentifier)}`, token);
      
      // Refresh team data
      const updatedTeam = await apiGet<Team>(`/teams/${teamId}`, token);
      setTeam(updatedTeam);
      
      // Reset upload error if it was set
      setUploadError('');
    } catch (error) {
      console.error('Error removing member:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to remove member');
    } finally {
      setRemoving('');
    }
  };

  const canRemoveMember = (member: TeamMember): boolean => {
    // Only organizers or team members can remove members
    if (!isOrganizer && !isTeamMember) return false;
    
    // Don't allow removing yourself if you're the creator and not an organizer
    if (member.user_id === user?.user_id || member.email === user?.email) {
      return isOrganizer; // Only organizers can remove themselves
    }
    
    return true;
  };

  const handleUploadSuccess = (message: string) => {
    setUploadSuccess(message);
    setUploadError('');
    // Clear success message after 5 seconds
    setTimeout(() => setUploadSuccess(''), 5000);
  };

  const handleUploadError = (errorMessage: string) => {
    setUploadError(`❌ Upload failed: ${errorMessage}`);
    setUploadSuccess('');
  };

  const handleMemberAdded = () => {
    // Reload team data to show the new member
    loadTeam();
    // Also refresh user team status after adding a member
    if (team?.event_id) {
      checkUserTeamStatus(team.event_id);
    }
  };

  const handleJoinTeam = async () => {
    if (!token || !user || !team) return;

    // First try to get user's existing location from their participant registration
    try {
      const userRegistrations = await apiGet<ParticipantResponse[]>('/events/me/registrations', token);
      const userParticipant = userRegistrations.find(p => p.event_id === team.event_id);
      
      if (userParticipant?.location) {
        // User has existing location data, proceed directly
        await joinTeamWithLocation(userParticipant.location, userParticipant.location_other);
      } else {
        // User needs to select location, show location selector
        setShowJoinLocationSelector(true);
      }
    } catch (error) {
      console.error('Error checking user registration:', error);
      // If we can't get registration data, show location selector
      setShowJoinLocationSelector(true);
    }
  };

  const joinTeamWithLocation = async (location: string, locationOther?: string) => {
    if (!token || !user || !team) return;

    setJoining(true);
    try {
      // Use the existing add team member API with user's location
      const addMemberRequest: AddTeamMemberRequest = {
        user_id: user.user_id,
        email: user.email,
        display_name: user.display_name,
        location: location,
        location_other: locationOther
      };

      await apiPost(`/teams/${team.team_id}/members`, addMemberRequest, token);
      
      // Refresh team data to show the new member
      const updatedTeam = await apiGet<Team>(`/teams/${teamId}`, token);
      setTeam(updatedTeam);
      
      // Refresh user data in auth context to update current teams across the app
      await refreshUser();
      
      // Update local user team status for this component
      checkUserTeamStatus(team.event_id);
      
      setUploadSuccess(`🎉 Successfully joined "${team.name}"!`);
      setUploadError('');
      
      // Clear success message after 5 seconds
      setTimeout(() => setUploadSuccess(''), 5000);
      
      // Close location selector if it was open
      setShowJoinLocationSelector(false);
      setJoinLocation('');
      setJoinLocationOther('');
      
    } catch (error) {
      console.error('Error joining team:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to join team';
      setUploadError(`❌ Failed to join team: ${errorMessage}`);
      setUploadSuccess('');
    } finally {
      setJoining(false);
    }
  };

  const handleJoinLocationSubmit = async () => {
    if (!joinLocation) {
      setUploadError('Please select a location');
      return;
    }
    
    if (joinLocation === 'Other' && !joinLocationOther) {
      setUploadError('Please specify your location');
      return;
    }
    
    await joinTeamWithLocation(joinLocation, joinLocationOther);
  };

  const handleJoinLocationCancel = () => {
    setShowJoinLocationSelector(false);
    setJoinLocation('');
    setJoinLocationOther('');
    setUploadError('');
  };

  const clearMessages = () => {
    setUploadSuccess('');
    setUploadError('');
  };

  // Helper function to determine if user can edit this team
  const canEditTeam = (): boolean => {
    return isOrganizer || isTeamMember;
  };

  // Handlers for team editing
  const handleEditTeam = () => {
    setShowEditTeam(true);
  };

  const handleCancelEdit = () => {
    setShowEditTeam(false);
  };

  const handleTeamUpdated = (updatedTeam: Team) => {
    setTeam(updatedTeam);
    setShowEditTeam(false);
  };

  const currentMemberCount = team?.member_count ?? 0;
  const teamCapacity = event?.max_team_size ?? 5;
  const isTeamFull = !!team && currentMemberCount >= teamCapacity;

  // Helper function to determine if user can join this team
  const canJoinTeam = (): boolean => {
    console.log('🔍 canJoinTeam: Evaluating join eligibility...');
    
    if (!user || !team) {
      console.log('❌ canJoinTeam: No user or team available');
      return false;
    }
    
    // Check for existing team in this event using auth context data first
    const userTeamInEvent = user.current_teams?.find(t => t.event_id === team.event_id);
    
    // Check if user is viewing their own team
    if (userTeamInEvent && userTeamInEvent.team_id === team.team_id) {
      console.log('❌ canJoinTeam: User is viewing their own team');
      return false;
    }
    
    // Don't show join button if user is already a team member
    if (isTeamMember) {
      console.log('❌ canJoinTeam: User is already a team member');
      return false;
    }
    
    // Don't show join button if user already has a team in this event
    if (userTeamInEvent) {
      console.log('❌ canJoinTeam: User already has a team in this event:', userTeamInEvent.name);
      return false;
    }
    
    // Participants can join any team, regardless of looking_for_members status
    // Only organizers are restricted from joining teams
    if (isOrganizer) {
      console.log('❌ canJoinTeam: User is an organizer');
      return false;
    }

    if (isTeamFull) {
      console.log('❌ canJoinTeam: Team is full');
      return false;
    }
    
    console.log('✅ canJoinTeam: User can join this team!');
    return true;
  };

  // Helper function to render event information
  const renderEventInfo = () => {
    if (eventLoading) {
      return <div>🎯 <strong>Event: Loading...</strong></div>;
    }
    
    if (event) {
      return (
        <div>
          🎯 <strong>Event: </strong>
          <Link 
            to={`/events/${event.event_id}`}
            style={{ 
              color: '#007bff', 
              textDecoration: 'none',
              fontWeight: 'bold'
            }}
          >
            {event.name}
          </Link>
        </div>
      );
    }
    
    // Fallback to event ID if event details failed to load
    return <div>🎯 <strong>Event ID: {team?.event_id}</strong></div>;
  };

  if (loading) {
    return <div className="loading">Loading team...</div>;
  }

  if (error) {
    return (
      <div className="error">
        {error}
        <Link to="/teams" className="btn btn-secondary" style={{ marginLeft: '10px' }}>
          Back to Teams
        </Link>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <h3>Team not found</h3>
        <Link to="/teams" className="btn">Back to Teams</Link>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <Link to="/teams" className="btn btn-secondary">← Back to Teams</Link>
      </div>
      
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
          <h1 style={{ margin: 0, flex: 1 }}>{team.name}</h1>
          {canEditTeam() && (
            <button
              onClick={handleEditTeam}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                marginLeft: '15px'
              }}
              title="Edit team details"
            >
              ✏️ Edit Team
            </button>
          )}
        </div>
        {team.description && (
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>{team.description}</p>
        )}
        
        {/* Team Leader section - commented out as requested */}
        {/*
        <div style={{ marginBottom: '30px' }}>
          <h3>👤 Team Leader</h3>
          <p>Created by user ID: {team.created_by}</p>
        </div>
        */}
        
        <div style={{ marginBottom: '30px' }}>
          <h3>👥 Members ({currentMemberCount}/{teamCapacity})</h3>
          {isTeamFull && (
            <p style={{ color: '#856404', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px', padding: '10px', marginBottom: '15px' }}>
              This team is currently full for the event capacity.
            </p>
          )}
          {team.members && team.members.length > 0 ? (
            <div style={{ display: 'grid', gap: '10px' }}>
              {team.members.map((member, index) => (
                <div key={`member-${member.user_id || member.email}`} className="member-card" style={{
                  padding: '10px',
                  border: '1px solid #eee',
                  borderRadius: '5px',
                  backgroundColor: '#f9f9f9',
                  position: 'relative'
                }}>
                  <strong>{member.display_name}</strong>
                  {member.email && <div style={{ fontSize: '14px', color: '#666' }}>{member.email}</div>}
                  <div style={{ fontSize: '12px', color: '#999' }}>Role: {member.role}</div>
                  
                  {/* Remove member button */}
                  {canRemoveMember(member) && (
                    <button
                      onClick={() => handleRemoveMember(member)}
                      disabled={removing === (member.user_id || member.email)}
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        background: '#ff4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        padding: '2px 6px',
                        fontSize: '12px',
                        cursor: removing === (member.user_id || member.email) ? 'not-allowed' : 'pointer',
                        opacity: removing === (member.user_id || member.email) ? 0.6 : 1
                      }}
                      title="Remove member"
                    >
                      {removing === (member.user_id || member.email) ? '...' : '×'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#666' }}>No members listed</p>
          )}
          
          {/* Add Team Member Component */}
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
            <AddTeamMember 
              teamId={team.team_id}
              onMemberAdded={handleMemberAdded}
              onError={handleUploadError}
              onSuccess={handleUploadSuccess}
              canJoinTeam={canJoinTeam()}
              canAddOthers={isOrganizer || isTeamMember}
              checkingTeamStatus={checkingTeamStatus}
              joining={joining}
              lookingForMembers={team.looking_for_members}
              onJoinTeam={handleJoinTeam}
              teamFull={isTeamFull}
              teamCapacity={teamCapacity}
              currentMemberCount={currentMemberCount}
            />
          </div>
        </div>
        
        {team.desired_skills && team.desired_skills.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <h3>🔍 Skills Needed</h3>
            <div>
              {team.desired_skills.map((skill: string, index: number) => (
                <span key={`skill-${skill}-${index}`} className="skill-badge" style={{
                  display: 'inline-block',
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '5px 12px',
                  borderRadius: '15px',
                  fontSize: '14px',
                  marginRight: '8px',
                  marginBottom: '5px'
                }}>
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}
        
        <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
          <h3>📊 Team Info</h3>
          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap', alignItems: 'center' }}>
            {renderEventInfo()}
            <div>📅 Created {new Date(team.created_at).toLocaleDateString()}</div>
            <div>🔍 Looking for members: {team.looking_for_members ? 'Yes' : 'No'}</div>
            <div>👥 Capacity: {currentMemberCount}/{teamCapacity}</div>
          </div>
          
          {/* AWS Account Assignment Display */}
          <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #f0f0f0' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              ☁️ AWS Account Assignment
            </h4>
            {team.aws_account_assignment ? (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                background: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '14px'
              }}>
                <span style={{
                  background: '#28a745',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  ASSIGNED
                </span>
                <span style={{ fontWeight: 'bold', color: '#155724' }}>
                  {team.aws_account_assignment}
                </span>
              </div>
            ) : (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                background: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '14px'
              }}>
                <span style={{
                  background: '#ffc107',
                  color: '#212529',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  PENDING
                </span>
                <span style={{ color: '#856404' }}>
                  Awaiting account assignment by organizers
                </span>
              </div>
            )}
          </div>

          {/* Debug Info - Remove this in production TODO: Tidy up/update to reflect correct data and hide behind feature flag */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{ 
              marginTop: '20px', 
              padding: '10px', 
              background: '#f8f9fa', 
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              fontSize: '12px',
              fontFamily: 'monospace'
            }}>
              <strong>Debug Info:</strong><br/>
              canJoinTeam(): {canJoinTeam().toString()}<br/>
              isTeamMember: {isTeamMember.toString()}<br/>
              isOrganizer: {isOrganizer.toString()}<br/>
              team.looking_for_members: {team?.looking_for_members?.toString()}<br/>
              userCurrentTeam: {userCurrentTeam ? userCurrentTeam.name : 'null'}<br/>
              checkingTeamStatus: {checkingTeamStatus.toString()}<br/>
              isViewingOwnTeam: {userCurrentTeam && userCurrentTeam.team_id === team?.team_id ? 'true' : 'false'}<br/>
              currentTeamId: {team?.team_id}<br/>
              userTeamId: {userCurrentTeam?.team_id || 'null'}<br/>
              authContextHasTeams: {user?.current_teams ? 'true' : 'false'}<br/>
              authContextTeamCount: {user?.current_teams?.length || 0}
              
              {!canJoinTeam() && (
                <>
                  <br/><br/><strong style={{ color: 'red' }}>Why can't join:</strong><br/>
                  {!user || !team ? '❌ No user or team data' : ''}
                  {userCurrentTeam && userCurrentTeam.team_id === team?.team_id ? '❌ Viewing own team' : ''}
                  {isTeamMember ? '❌ Already team member' : ''}
                  {userCurrentTeam && userCurrentTeam.team_id !== team?.team_id ? `❌ Has team: ${userCurrentTeam.name}` : ''}
                  {!team?.looking_for_members ? '❌ Team not looking for members' : ''}
                  {isTeamFull ? '❌ Team is full' : ''}
                  {isOrganizer ? '❌ User is organizer' : ''}
                </>
              )}
            </div>
          )}
          
          {/* Feature Flag Status - Remove this in production */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{ marginTop: '20px' }}>
              <FeatureFlagToggle 
                flagName="hackathonmgr_fileupload"
                title="File Upload Feature"
                description="Controls visibility of the file upload section below"
              />
            </div>
          )}
        </div>

        {/* File Upload Section - Feature flag controlled */}
        {fileUploadEnabled && (
          <div style={{ marginTop: '40px', paddingTop: '30px', borderTop: '2px solid #eee' }}>
            {(uploadSuccess || uploadError) && (
              <div style={{ 
                marginBottom: '20px',
                padding: '12px',
                borderRadius: '6px',
                backgroundColor: uploadSuccess ? '#d4edda' : '#f8d7da',
                color: uploadSuccess ? '#155724' : '#721c24',
                border: `1px solid ${uploadSuccess ? '#c3e6cb' : '#f5c6cb'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{uploadSuccess || uploadError}</span>
                <button
                  onClick={clearMessages}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    padding: '0 5px'
                  }}
                >
                  ✕
                </button>
              </div>
            )}

            <TeamFileManager
              teamId={team.team_id}
              onError={handleUploadError}
              onSuccess={setUploadSuccess}
            />
          </div>
        )}
      </div>

      {/* Edit Team Modal */}
      {showEditTeam && team && (
        <EditTeam
          team={team}
          onTeamUpdated={handleTeamUpdated}
          onCancel={handleCancelEdit}
          onError={handleUploadError}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* Location Selection Modal for Joining Team */}
      {showJoinLocationSelector && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            minWidth: '400px',
            maxWidth: '500px'
          }}>
            <h3>Select Your Location</h3>
            <p>Please select your location for merch and catering purposes:</p>
            
            <LocationSelector
              location={joinLocation}
              locationOther={joinLocationOther}
              onLocationChange={setJoinLocation}
              onLocationOtherChange={setJoinLocationOther}
            />
            
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleJoinLocationCancel}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleJoinLocationSubmit}
                disabled={joining}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  cursor: joining ? 'not-allowed' : 'pointer',
                  opacity: joining ? 0.6 : 1
                }}
              >
                {joining ? 'Joining...' : 'Join Team'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDetails;
